<template>
  <div class="checkout-page">
    <div class="checkout-main">
      <!-- Left: Book info card -->
      <div class="checkout-book">
        <!-- Skeleton loading -->
        <div class="checkout-book__skeleton" v-if="!bookLoaded">
          <div class="skeleton-cover"></div>
          <div class="skeleton-info">
            <div class="skeleton-line skeleton-line--title"></div>
            <div class="skeleton-line skeleton-line--sub"></div>
          </div>
          <div class="skeleton-details">
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line skeleton-line--short"></div>
          </div>
        </div>
        <div class="checkout-book__top" v-if="bookLoaded">
          <div class="checkout-book__cover">
            <img v-if="book.coverUrl" :src="book.coverUrl" alt="cover">
          </div>
          <div class="checkout-book__info">
            <h3 class="checkout-book__title">{{book.design ? book.design.title : ''}}</h3>
            <p class="checkout-book__author">{{book.design ? book.design.author : ''}}</p>
          </div>
        </div>
        <div class="checkout-book__details" v-if="bookLoaded">
          <div class="checkout-book__row"><span>{{$t('checkout.labelTypeset')}}</span><span class="checkout-tag">{{$te(book.template) ? $t(book.template) : book.template}}</span></div>
          <div class="checkout-book__row"><span>{{$t('checkout.labelPages')}}</span><strong>{{book.totalPage}} {{$t('checkout.labelPagesUnit')}}</strong></div>
          <div class="checkout-book__row" v-if="book.typeset"><span>{{$t('checkout.labelWidth')}}</span><strong>{{bookSize.width}} mm</strong></div>
          <div class="checkout-book__row" v-if="book.typeset"><span>{{$t('checkout.labelHeight')}}</span><strong>{{bookSize.height}} mm</strong></div>
        </div>
      </div>

      <!-- Right: Order form -->
      <div class="checkout-form">
        <h2 class="checkout-form__title">{{$t('checkout.orderTitle')}}</h2>

        <!-- Binding type -->
        <div class="checkout-section" v-if="bookType === 'diarybook'">
          <div class="checkout-section__header">
            <label>{{$t('checkout.labelBinding')}}</label>
            <small class="checkout-link" v-if="priceLoaded" @click="showBinding = true">
              {{$t('checkout.labelTipBinding')}}
            </small>
          </div>
          <div class="checkout-binding">
            <template v-for="(binding, index) in bindings">
              <div v-if="binding.default" :key="index"
                   :class="['checkout-binding__item', { active: binding.name === order.binding }]"
                   @click="order.binding = binding.name">
                {{$t(binding.desc)}}
              </div>
            </template>
          </div>
        </div>

        <!-- Quantity -->
        <div class="checkout-section" v-show="order.binding !== 'pdf'">
          <label>{{$t('checkout.labelQuantity')}}</label>
          <order-num :number="order.quantity" :perCost="perCost" :symbol="symbol" @change="setOrderQuantity"/>
        </div>

        <!-- PDF email -->
        <div ref="pdfaddress" class="checkout-section pdf-email" v-show="order.binding === 'pdf'">
          <label>{{$t('address.labelEmail')}} *</label>
          <input type="text" class="form-control" v-model.trim="order.pdfEmail">
        </div>

        <!-- Shipping info -->
        <div class="checkout-section" v-show="order.binding !== 'pdf'">
          <label>{{$t('checkout.labelShipping')}}</label>
          <order-address :activeAddressId="address.addressId"
                         ref="address"
                         :addressTip="addressTip"
                         :addresses="addresses"
                         @change="changeAddress"></order-address>
          <!-- Taiwan customs info -->
          <div class="checkout-idinfo" v-if="shouldIdInfoShow">
            <label>實名信息</label>
            <button class="btn plant btn-sm" @click="editIdInfo">點擊編輯</button>
            <span v-if="idInfo.customsName" class="checkout-idinfo__name">{{idInfo.customsName}}</span>
          </div>
        </div>

        <!-- Coupon -->
        <div class="checkout-section">
          <label>{{$t('checkout.labelCoupon')}}</label>
          <!-- Manual input -->
          <div class="checkout-coupon-input">
            <input type="text" class="form-control" v-model.trim="couponCode"
                   :placeholder="$t('checkout.couponInputPlaceholder')">
            <button class="btn primary btn-sm" @click="applyCouponCode" :disabled="!couponCode">
              {{$t('checkout.couponApply')}}
            </button>
          </div>
          <!-- Auto-detected coupons -->
          <div class="checkout-coupon-auto" v-if="couponLoaded && coupons.length">
            <div class="checkout-coupon-auto__label">
              <span>{{$t('checkout.couponAvailable', { count: coupons.length })}}</span>
              <button class="checkout-link" @click="showCoupon = true">
                {{$t('checkout.couponSelect')}}
              </button>
            </div>
          </div>
          <!-- Active coupon display -->
          <div class="checkout-coupon-active" v-if="activeCoupon.couponNo">
            <div class="checkout-coupon-active__info">
              <i class="fa fa-ticket"></i>
              <span>{{couponTip}}</span>
            </div>
            <button class="checkout-coupon-active__remove" @click="forgiveCoupon">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <popup :show="showCoupon" @close="showCoupon = false" :showClose="false">
            <div slot="body" class="coupon-popup-body">
              <h3 class="text-center" style="font-size: 18px; font-weight: 600; margin: 8px 0 20px;">{{$t('checkout.labelCouponTitle')}}</h3>
              <div class="text-center" v-if="!coupons.length">
                {{$t('common.toast.tip.couponEmpty')}}
              </div>
              <coupon v-for="(coupon, index) in coupons" :available="true"
                      :active="activeCoupon.couponNo === coupon.couponNo"
                      :coupon="coupon" :key="index" @use="useCoupon"/>
            </div>
          </popup>
        </div>

        <!-- Payment method -->
        <div class="checkout-section">
          <label>{{$t('checkout.labelMethod')}}</label>
          <div class="checkout-pay-methods">
            <div :class="['checkout-pay-method', { active: order.payMethod === 'stripe' }]"
                 @click="seMethod('stripe')">
              <span style="font-size: 12px;">{{$t('checkout.labelMethodStripe')}}</span>
              <span class="checkout-pay-cards">
                <img src="../../../../static/images/payment/visa-curved-128px.png" alt="Visa">
                <img src="../../../../static/images/payment/mastercard-curved-128px.png" alt="Mastercard">
                <img src="../../../../static/images/payment/american-express-curved-128px.png" alt="Amex">
              </span>
            </div>
            <span class="checkout-pay-sep">|</span>
            <div :class="['checkout-pay-method', { active: order.payMethod === 'paypal' }]"
                 @click="seMethod('paypal')">
              <img src="../../../../static/images/payment/paypal-curved-128px.png" alt="PayPal" class="checkout-pay-icon">
              <span style="font-size: 12px;">PayPal</span>
            </div>
          </div>
        </div>

        <!-- Delivery (hidden, info moved to footer) -->

        <!-- Note -->
        <div class="checkout-section">
          <label>{{$t('checkout.labelNote')}}</label>
          <textarea class="form-control checkout-note" name="note" rows="1"
                    :placeholder="$t('checkout.labelNotePlaceholder')"
                    v-model="order.note"></textarea>
        </div>

        <!-- Tips (collapsible) -->
        <div class="checkout-section checkout-tips">
          <div class="checkout-tips__header" @click="showTips = !showTips">
            <label>{{$t('checkout.tipTitle')}}</label>
            <i :class="['fa', showTips ? 'fa-angle-up' : 'fa-angle-down']"></i>
          </div>
          <div class="checkout-tips__body" v-show="showTips">
            <p v-for="(i, idx) in tips" :key="'tip' + idx" v-html="(idx + 1) + '. ' + i"></p>
            <template v-if="order.binding !== 'pdf'">
              <p v-if="address && address.countryCode === 'TW'">
                <b class="text-primary">因清關需要實名驗證，請提前下載易利委(EZway)APP進行註冊，否則書籍無法過關。</b>
              </p>
              <p v-html="$t('checkout.tipFreeExpress')"
                 v-show="bookLoaded && book.totalPage * order.quantity >= 500"></p>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Price summary + submit — fixed bottom bar -->
    <div class="checkout-footer">
      <div class="checkout-footer__inner">
        <!-- Desktop: price items inline -->
        <div class="checkout-price__items hidden-xs">
          <span class="checkout-price__item">{{$t('checkout.labelSumBook')}} {{symbol}}{{rawBookCost.toFixed(2)}}</span>
          <span class="checkout-price__item text-green" v-if="discount > 0">{{$t('checkout.labelSumDiscount')}} -{{symbol}}{{discount.toFixed(2)}}</span>
          <span class="checkout-price__item">{{$t('checkout.labelSumExpress')}} {{symbol}}{{deliveryCost.toFixed(2)}}</span>
        </div>
        <!-- Mobile: expandable price details -->
        <div class="checkout-footer__m-left visible-xs">
          <div class="checkout-footer__m-total" @click="showMobilePriceDetail = !showMobilePriceDetail">
            <span>{{$t('checkout.labelSumTotal')}}</span>
            <strong>{{symbol}}{{totalCost}}</strong>
            <i :class="['fa', showMobilePriceDetail ? 'fa-caret-down' : 'fa-caret-up']"></i>
          </div>
          <div class="checkout-footer__m-delivery" v-if="order.binding !== 'pdf'">
            {{$t('checkout.labelExpressPlaceholder')}}
          </div>
          <div class="checkout-footer__m-details" v-show="showMobilePriceDetail">
            <span>{{$t('checkout.labelSumBook')}} {{symbol}}{{rawBookCost.toFixed(2)}}</span>
            <span class="text-green" v-if="discount > 0">{{$t('checkout.labelSumDiscount')}} -{{symbol}}{{discount.toFixed(2)}}</span>
            <span>{{$t('checkout.labelSumExpress')}} {{symbol}}{{deliveryCost.toFixed(2)}}</span>
          </div>
        </div>
        <div class="checkout-footer__right">
          <div class="checkout-footer__total hidden-xs">
            <span>{{$t('checkout.labelSumTotal')}}</span>
            <strong>{{symbol}}{{totalCost}}</strong>
          </div>
          <div class="checkout-footer__meta hidden-xs" v-if="order.binding !== 'pdf'">
            {{$t('checkout.labelExpressPlaceholder')}}
          </div>
          <button class="btn primary btn-lg checkout-submit__btn"
                  :class="{'is-disabled': !addressLoaded || !priceLoaded || !bookLoaded || isReTypeset}"
                  @click="startCheck">
            <i class="fa fa-shopping-cart"></i> {{$t('checkout.buttonSubmit')}}
          </button>
        </div>
      </div>
    </div>

    <modal :show="showIdInfo">
      <template #title>實名信息</template>
      <div slot="body">
        <div>
          <div class="form-group">
            <label>收件姓名</label>
            <input type="text" class="form-control" placeholder="請填寫正確中文姓名（必填）"
                   v-model.trim="idInfo.customsName">
          </div>
          <div class="form-group">
            <label>身份證號</label>
            <input type="text" class="form-control" placeholder="請填寫正確的身份ID（必填）"
                   v-model.trim="idInfo.idNumber">
          </div>
          <div class="form-group">
            <label>手機號</label>
            <input type="text" class="form-control" placeholder="請填寫可聯繫的手機號/電話號碼（必填）"
                   v-model="idInfo.customsPhone">
          </div>
        </div>
        <p class="mt-1">
          <b
            class="text-primary text-small">溫馨提示：因為海關對快件的收方信息嚴格審查，台灣用戶請您在實名認證平台（易利委APP）實名認證，否則無法寄件，以上信息不會用於其它地方，請放心填寫。</b>
        </p>
      </div>
      <template #footer>
        <div>
          <button class="btn default" @click="showIdInfo = false">
            取消
          </button>
          <button class="btn primary" @click="updateIdInfo">
            完成
          </button>
        </div>
      </template>
    </modal>

    <!-- StripeModal removed — using Stripe Checkout (hosted payment page) -->
    <i-dialog :show="showBinding" @close="showBinding = false">
      <div style="position: relative;">
        <button class="dialog-close-btn" @click="showBinding = false">
          <i class="fa fa-times"></i>
        </button>
        <order-binding :isA4="isA4"></order-binding>
      </div>
    </i-dialog>
    <toast :message="paidLoading.text" :value="paidLoading.show" :lock="true" closeable>
      <div class="loading-icon"></div>
    </toast>
  </div>
</template>

<script>
// OrderInfo replaced with inline book card in template
import OrderAddress from './OrderAddress.vue'
import OrderNum from './OrderNum.vue'
// PayMethods replaced with inline buttons in template
import Coupon from 'src/components/layout/Coupon'
import OrderBinding from '../Trade/OrderBinding.vue'
// StripeModal removed — using Stripe Checkout (hosted payment page)
import { cloneDeep, filter, find, isEmpty, isNaN, orderBy } from 'lodash'
// load-script removed — no longer loading Stripe.js client-side
import api from 'src/api/'
import { track } from 'src/utils/track'
import generateCover from 'src/_utils/generateCover'
import config from 'src/app.config'
import currency from 'src/mixins/currency'
import { mapActions, mapGetters } from 'vuex'
import Cookie from 'js-cookie'

export default {
  name: 'check',
  mixins: [currency],
  components: {
    OrderAddress,
    OrderNum,
    Coupon,
    OrderBinding
  },
  data () {
    return {
      bookType: 'diarybook',
      book: {},
      bindings: config.bindings,
      order: {
        quantity: 1,
        binding: 'hardback',
        payMethod: 'stripe',
        delivery: '3-day-delivery',
        note: '',
        pdfEmail: ''
      },
      stripeKey: '',
      prices: {
        book: {},
        delivery: {}
      }, // 当前地区价格表 {book:{}, delivery: {}}
      priceTables: {},
      albumTables: {}, // 相片书价格表
      calendarTables: {}, // 台历价格表
      coupons: [], // 当前全部个人优惠信息
      activeCoupon: {}, //
      addresses: [], // 全部地址
      address: {}, // 最新使用地址
      isA4: false,
      isReTypeset: false,
      showMenu: false,
      showTrade: false,
      bookLoaded: false,
      priceLoaded: false,
      couponLoaded: false,
      addressLoaded: false,
      showModal: false,
      showCoupon: false,
      showBinding: false,
      showIdInfo: false,
      couponCode: '',
      showTips: true,
      showMobilePriceDetail: false,
      idInfo: {},
      loading: {
        text: '支付中',
        visible: false
      },
      paidLoading: {
        text: '',
        show: false
      }
    }
  },
  watch: {
    order: {
      handler (newVal, oldVal) {
        if (newVal.pdfEmail !== '') {
          const addressEl = this.$refs.pdfaddress
          if (addressEl.classList.contains('error-item')) {
            addressEl.classList.remove('error-item')
          }
        }
      },
      deep: true
    },
    address (state, preState) {
      if (!isEmpty(state)) {
        const locale = this.$store.state.locale
        const addressEl = this.$refs.address.$el
        if (addressEl.classList.contains('error-item')) {
          addressEl.classList.remove('error-item')
        }
        if (state.countryCode !== locale && state.countryCode) {
          console.log(`当前地址变化为：${state.countryCode}`)
          this.setLocale(state.countryCode)
          this.fetchPrice(state.countryCode.toLowerCase())
        }
      }
    },
    bookLoaded (state) {
      if (state && this.couponLoaded) {
        this.autoSelectCoupon()
      }
    },
    couponLoaded (state) {
      if (state && this.bookLoaded) {
        this.autoSelectCoupon()
      }
    },
    showIdInfo (value) {
      if (!value) {
        if (!this.address.idNumber) {
          this.idInfo = {}
        } else {
          const {
            customsName,
            customsPhone,
            idNumber
          } = this.address
          this.idInfo = {
            customsName,
            customsPhone,
            idNumber
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'language', 'locale'
    ]),
    supportMethods () {
      return [
        {
          type: 'stripe',
          label: '信用卡支付',
          visible: true
        },
        {
          type: 'paypal',
          label: 'PayPal 支付',
          visible: true
        },
        {
          type: 'alipay',
          label: '支付宝',
          visible: false
        }
      ]
    },
    shouldIdInfoShow () {
      return this.order.binding !== 'pdf' && this.address && this.address.countryCode === 'TW'
    },
    bookSize () {
      if (!this.book.typeset) return { width: 0, height: 0 }
      const sizes = {
        'a5-1': { width: 148, height: 210 },
        'a5-2': { width: 148, height: 210 },
        'a4-1': { width: 210, height: 279 },
        'a4-2': { width: 210, height: 279 }
      }
      return sizes[this.book.typeset] || { width: 148, height: 210 }
    },
    // 多本无折扣活动 —— 数量分档 discount 全部 1，保留 computed 是为了
    // 不破坏其他可能引用它的地方（例如 customDiscount 显示）
    quantityTiers () {
      return [
        { min: 1, max: Infinity, discount: 1, label: '' }
      ]
    },
    quantityDiscount () {
      return 1
    },
    rawBookCost () {
      // 多本直接 perCost * quantity，没有折扣
      return this.perCost * this.order.quantity
    },
    quantityDiscountAmount () {
      return 0
    },
    perCost () { // 单品价格
      if (!this.priceLoaded) {
        return 0
      }
      if (!this.bookLoaded) {
        return 0
      }

      const totalPages = Math.max(this.book.totalPage, config.minBookPagesLimitation)

      if (this.bookType === 'photobook') {
        const { template, design } = this.book
        const { cover } = design
        const material = cover.material || 'fabric'
        const priceMaps = this.albumTables[template][material]
        return Number(priceMaps.cover) + Number(priceMaps.inner) * totalPages
      }

      if (this.bookType === 'calendar') {
        const { template } = this.book
        return Number(this.calendarTables[template])
      }

      // 根据版式选择对应价格表（A4 或 A5）
      const typeset = this.book.typeset ? this.book.typeset.substr(0, 2) : 'a5'
      const priceMaps = (this.priceTables[typeset] && this.priceTables[typeset][this.order.binding]) ||
                         this.prices.book[this.order.binding]
      return Number(priceMaps.cover) + Number(priceMaps.inner) * totalPages
    },
    bookCost () {
      if (!this.priceLoaded) {
        return 0
      }
      if (!this.bookLoaded) {
        return 0
      }
      // 计算单品价格
      try {
        const bookPages = Math.max(this.book.totalPage, config.minBookPagesLimitation)
        let page = bookPages ? bookPages * this.order.quantity : 0

        if (this.bookType === 'photobook') {
          const { template, design } = this.book
          const { cover } = design
          const material = cover.material || 'fabric'
          const priceMaps = this.albumTables[template][material]
          return Number(priceMaps.cover) * this.order.quantity + Number(priceMaps.inner) * page
        }

        if (this.bookType === 'calendar') {
          const { template } = this.book
          return Number(this.calendarTables[template]) * this.order.quantity
        }

        const typeset = this.book.typeset.substr(0, 2)
        const priceMaps = this.priceTables[typeset][this.order.binding]
        const isVoucher = !isEmpty(this.activeCoupon) && this.activeCoupon.type === 'voucher'
        const pageLimit = isVoucher && this.activeCoupon.rules.minPages ? Math.max(
          this.activeCoupon.rules.minPages,
          config.minBookPagesLimitation
        ) : config.minBookPagesLimitation
        page = Math.max(page, pageLimit)
        return Number(priceMaps.cover) * this.order.quantity + Number(priceMaps.inner) * page
      } catch (e) {
        console.error(e)
        return 0
      }
    },
    deliveryCost () {
      if (!this.priceLoaded) {
        return 0
      }
      // 添加临时活动 总页数500页 免运费
      if (this.book.totalPage * this.order.quantity >= 500) {
        return 0
      }

      // 免邮选项 ，台历产品暂时免邮
      if (this.couponLoaded &&
        ((!isEmpty(this.activeCoupon) && this.activeCoupon.rules.freeDelivery) || this.bookType === 'calendar')) {
        return 0
      }
      if (this.order.binding === 'pdf') {
        return this.prices.delivery['0-day-delivery'] - 0
      } else {
        return this.prices.delivery[this.order.delivery] - 0
      }
    },
    discount () {
      let typeset, priceMaps, page, pageLimit
      if (this.couponLoaded && !isEmpty(this.activeCoupon)) {
        switch (this.activeCoupon.type) {
          case 'discount':
            return this.bookCost * (1 - this.activeCoupon.value)
          case 'voucher':
            typeset = this.book.typeset.substr(0, 2)
            priceMaps = this.priceTables[typeset][this.order.binding]
            page = this.book.totalPage ? this.book.totalPage * this.order.quantity : 0
            pageLimit = this.activeCoupon.rules.minPages ? Math.max(
              this.activeCoupon.rules.minPages,
              config.minBookPagesLimitation
            ) : config.minBookPagesLimitation
            page = Math.max(page, pageLimit)
            page = Math.min(page, this.activeCoupon.value)
            return (priceMaps.cover - 0) + (priceMaps.inner - 0) * page
          case 'cash':
            return this.activeCoupon.value
          case 'bulk':
            if (this.order.quantity >= 2) {
              return Math.floor(this.order.quantity / 2) * (1 - this.activeCoupon.value) * this.perCost
            } else {
              return 0
            }
          default:
            return 0
        }
      } else {
        return 0
      }
    },
    totalCost () {
      // 多本无折扣 —— 直接 rawBookCost + 运费 - 优惠券
      return Math.max(0, this.rawBookCost + this.deliveryCost - this.discount).toFixed(2)
    },
    couponTip () {
      if (this.couponLoaded) {
        if (this.activeCoupon.couponNo) {
          const expressTip = this.activeCoupon.rules.freeDelivery ? this.$t('coupons.labelFreeDelivery') : ''
          const prefix = this.activeCoupon.rules.typeset ? this.$t(
            'checkout.tipCouponTypeset',
            { typeset: this.activeCoupon.rules.typeset }
          ) : ''
          switch (this.activeCoupon.type) {
            case 'discount':
              return this.activeCoupon.value ? `${prefix} ${this.$t(
                'checkout.tipCouponDiscount',
                { count: 100 - this.activeCoupon.value * 100 }
              )} ${expressTip}` : prefix + this.$t('checkout.labelCouponFree')
            case 'voucher':
              return `${prefix} ${this.$t(
                'checkout.tipCouponVoucher',
                { page: this.activeCoupon.value }
              )} + ${expressTip}`
            case 'cash':
              return `${prefix} ${this.symbol}${this.activeCoupon.value} + ${expressTip}`
            case 'bulk':
              return `${prefix} ${this.$t(
                'checkout.tipCouponBulk',
                { count: Math.round(100 - this.activeCoupon.value * 100) }
              )}`
          }
        }
        // 检查当前优惠券列表内，实际可用数
        const availableCoupons = this.coupons.filter(item => {
          const itemDiscount = item.type === 'discount' && item.value === 1 // 当前后端显示折扣 1，隐藏优惠
          const itemAvailable = !item.rules.groupAvaliable // 当前团购券不可用
          if (!(item.scope === 'group_buying' && (itemDiscount || itemAvailable))) {
            return item
          }
        })
        return availableCoupons.length ? this.$t(
          'checkout.labelCouponPlaceholder',
          { count: availableCoupons.length }
        ) : this.$t('checkout.labelCouponEmpty')
      }
      return this.$t('checkout.labelCouponEmpty')
    },
    addressTip () {
      if (isEmpty(this.address)) {
        return {
          receiver: '',
          address: ''
        }
      }
      return {
        receiver: `${this.address.lastName} ${this.address.firstName}, ${this.address.phone}`,
        address: [this.address.country, this.address.state, this.address.city, this.address.line1, this.address.line2].filter(Boolean).join(', ')
      }
    },
    tips () {
      let lines = [
        'checkout.tipLine1',
        'checkout.tipLine2',
        'checkout.tipLine3',
        'checkout.tipLine4',
        'checkout.tipLine5',
        'checkout.tipLine6'
      ]
      if (this.order.binding === 'pdf') {
        lines = lines.slice(0, 3)
      }
      return lines.map(k => this.$te(k) ? this.$t(k) : '').filter(Boolean)
    }
  },
  created () {
    const bid = this.$route.params.bid || this.$route.query.bid
    const method = this.$route.query.method
    const locale = (this.locale || 'TW').toLowerCase()
    this.bookType = this.$route.query.bookType || 'diarybook'
    this.fetchBook(bid)
    this.fetchPrice(locale)
    this.fetchOrderAddress()
    this.fetchCoupons()
    if (find(this.supportMethods, { type: method })) {
      this.order.payMethod = method
    }
    // GA4 漏斗：进入下单页（begin_checkout）
    track('begin_checkout', {
      currency: this.currency,
      value: Number(this.totalCost || 0),
      items: [{ item_id: String(bid), item_category: this.bookType }]
    })
  },
  mounted () {
    // Stripe Checkout: no client-side Stripe init needed (hosted payment page)
  },
  methods: {
    ...mapActions([
      'setLocale'
    ]),
    autoSelectCoupon () {
      if (this.bookType === 'diarybook') {
        const typeset = this.book.typeset.toUpperCase()
        const restCoupons = this.coupons.filter(coupon => {
          return coupon.bookType === 'all' || (coupon.bookType === 'diarybook' && !coupon.rules.typeset) ||
            (coupon.rules.typeset && typeset.includes(coupon.rules.typeset.toUpperCase()))
        }).sort((a, b) => a.value - b.value)
        if (restCoupons.length) {
          this.activeCoupon = restCoupons[0]
        }
      } else if (this.bookType === 'photobook' || this.bookType === 'calendar') {
        const restCoupons = this.coupons
          .filter(item => item.bookType === this.bookType || item.bookType === 'all')
          .sort((a, b) => a.value - b.value)
        if (restCoupons.length) {
          this.activeCoupon = restCoupons[0]
        }
      }
    },
    async applyCouponCode () {
      if (!this.couponCode) return
      const enteredCode = this.couponCode
      try {
        const data = await api.checkCoupon(enteredCode)
        if (!data || data.errors || isEmpty(data)) {
          this.$toast({ message: this.$t('checkout.couponInvalid'), position: 'bottom' })
          return
        }
        // checkCoupon returns coupon object
        const coupon = data.coupon || data
        this.useCoupon(coupon)
        this.couponCode = ''
        // GA4 funnel: 用户成功输入并应用优惠码
        track('add_coupon', { coupon: enteredCode })
      } catch (e) {
        this.$toast({ message: this.$t('checkout.couponInvalid'), position: 'bottom' })
      }
    },
    forgiveCoupon () {
      this.activeCoupon = {}
      Cookie.set('check_tip', true)
    },
    fetchBook (bid) {
      const bookType = this.bookType || 'diarybook'
      const requestHandler = {
        diarybook: api.fetchBook,
        photobook: api.fetchPhotoBook,
        calendar: api.fetchCalendar
      }
      const request = requestHandler[bookType]
      request(bid).then(data => {
        if (!data || data.errors) {
          return
        }
        if (this.bookType === 'diarybook') {
          const {
            retypesetMonths,
            typeset,
            source
          } = data
          const retypesetPages = retypesetMonths[typeset]
          this.isReTypeset = retypesetPages.length
          if (retypesetPages.length) {
            this.$confirm({
              title: '書籍排版中，請前往預覽檢查內容後再支付',
              cancel: false,
              okText: '前往預覽',
              onCancel: (modal) => {
                modal.close()
                this.$router.replace({
                  name: 'preview',
                  query: {
                    sid: source,
                    bid
                  }
                })
              }
            })
          }
        }
        if (this.bookType === 'diarybook') {
          data.template = find(config.templates, { name: data.typeset }).desc
        } else if (this.bookType === 'photobook') {
          data.template = data.template.name
          if (data.template === 'album') {
            this.order.delivery = 'album-delivery'
          }
        } else if (this.bookType === 'calendar') {
          data.template = data.template.name
          data.totalPage = 13
          data.design = {
            ...data.cover,
            cover: data.cover,
            template: data.cover.coverTemplate
          }
        }
        data.coverUrl = generateCover({
          design: data.design,
          chapters: data.currentChapters,
          isCalendar: this.bookType === 'calendar'
        })
        if (/A4/gi.test(data.typeset)) {
          this.order.binding = 'paperback'
          this.isA4 = true
        }
        data.type = this.bookType
        this.book = data
        this.bookLoaded = true
      })
    },
    fetchPrice (locale) {
      this.priceLoaded = false
      api.fetchPrice(locale).then(data => {
        this.priceLoaded = true
        this.prices = this.handleLocalePrices(data)
      })
    },
    fetchOrderAddress () {
      this.addressLoaded = false
      api.fetchOrderAddresses('delivery').then(data => {
        this.addressLoaded = true
        const addresses = filter(data, { countryCode: this.locale })
        if (addresses.length) {
          const address = orderBy(addresses, ['updatedAt'], ['desc'])[0]
          this.address = address
          if (address.idNumber) {
            this.idInfo = {
              idNumber: address.idNumber,
              customsName: address.customsName,
              customsPhone: address.customsPhone
            }
          }
        }
        this.addresses = data
      })
    },
    fetchCoupons () {
      api.fetchAllCoupon().then(data => {
        let coupons = data.map(item => {
          item.coupon.rest = item.accountCoupon.rest || item.coupon.rest
          item.coupon.used = item.accountCoupon.used || item.coupon.used
          return item.coupon
        })
        if (coupons.length) {
          coupons = coupons.filter(item => (item.scope !== 'group_buying' || item.rules.groupAvaliable) && Date.now() >=
            new Date(item.beginAt).getTime())
        }
        const sortKeys = ['all', 'diarybook', 'photobook', 'calendar']
        const sortObj = coupons.reduce((sortMap, coupon) => {
          if (!sortMap[coupon.bookType]) {
            sortMap[coupon.bookType] = []
          }
          sortMap[coupon.bookType].push(coupon)
          return sortMap
        }, {})
        coupons = sortKeys.reduce((list, key) => {
          if (sortObj[key]) {
            list = list.concat(sortObj[key])
          }
          return list
        }, [])
        this.coupons = coupons
        this.couponLoaded = true
      })
    },
    editIdInfo () {
      this.showIdInfo = true
    },
    updateIdInfo () {
      if (!this.idInfo.customsName || !this.idInfo.idNumber || !this.idInfo.customsPhone) {
        this.$toast({
          message: '請完善個人信息'
        })
        return
      }
      // 实名信息姓名中文限制
      if (!/[\u4e00-\u9fa5]+/g.test(this.idInfo.customsName)) {
        this.$toast({
          message: '請填寫正確中文姓名'
        })
        return
      }
      // 台湾电话校验
      const { countryCode } = this.address
      if (countryCode === 'TW' && !/^[0]?[1-9]{1}[0-9]{2}[0-9]{3}[0-9]{3}$/.test(this.idInfo.customsPhone)) {
        this.$confirm({
          title: '為保證您能順利收到快遞，請填寫正確手機/電話號碼',
          cancel: false
        })
        return
      }
      api.updateIdInfo({
        ...this.idInfo,
        countryCode
      }).then(data => {
        if (data.errors) {
          this.$toast({
            message: data.errors.msg
          })
          return
        }
        this.address = { ...this.address, ...this.idInfo }
        this.$nextTick(() => {
          this.showIdInfo = false
        })
      })
    },
    postItem () {
      // GA4 漏斗：begin_checkout 已在 created() 触发，这里保留方法签名为空以兼容旧调用
    },
    // initStripe removed — using Stripe Checkout (hosted payment page)
    // 处理本地价格
    handleLocalePrices (prices) {
      const bookPrice = find(prices, { priceType: 'book' })
      const deliveryPrice = find(prices, { priceType: 'delivery' })
      const photoBookPrice = find(prices, { priceType: 'photobook' }) // 相片书价格
      const calendarPrice = find(prices, { priceType: 'calendar' }) // 台历价格表
      if (isEmpty(bookPrice.value) || isEmpty(deliveryPrice.value)) {
        this.$toast({
          message: this.$t('checkout.tipPriceFailed'),
          position: 'bottom'
        })
        return {}
      }
      const a5Price = bookPrice.value.a5
      const priceTables = cloneDeep(bookPrice.value)
      Object.keys(priceTables).map(item => {
        Object.keys(priceTables[item]).map(price => {
          priceTables[item][price].inner = priceTables[item][price].inner[200]
        })
      })
      this.priceTables = priceTables
      if (photoBookPrice) {
        // console.log(photoBookPrice)
        this.albumTables = photoBookPrice.value
      }
      if (calendarPrice) {
        this.calendarTables = calendarPrice.value
      }
      try {
        Object.keys(a5Price).map((item) => {
          a5Price[item].inner = a5Price[item].inner[200] // 暂时取200的价格
        })
      } catch (e) {
        console.error(e)
      }
      return {
        book: a5Price,
        delivery: deliveryPrice.value
      }
    },
    editAddress () {
      const query = Object.assign({}, this.$route.query, { ads: 1 })
      if (!isEmpty(this.address)) query.aid = this.address.addressId
      this.$router.push({
        path: this.$route.path,
        query: query
      })
    },
    changeAddress (address) { // 注意切换地区应该产生价格变化
      if (!isEmpty(address)) {
        this.address = { ...address }
      }
    },
    setOrderQuantity (num) {
      this.order.quantity = num
      if ((this.activeCoupon.maxPages && this.activeCoupon.maxBooks) && (num > this.activeCoupon.maxBooks)) {
        this.activeCoupon = {}
      }

      // 免单券用户限制一本
      if (!isEmpty(this.activeCoupon) && !this.activeCoupon.value && num > 1) {
        this.activeCoupon = {}
        this.$toast({
          message: '免單優惠券僅限一本書籍',
          duration: 3500
        })
        return
      }
      if (num <= 1 && this.activeCoupon.type === 'bulk') {
        this.activeCoupon = {}
        this.$toast({
          message: '當前優惠券僅支持兩本以上訂單',
          duration: 3500
        })
      }
    },
    seMethod (method) {
      this.order.payMethod = method
    },
    startCheck () {
      if (!this.bookLoaded || !this.addressLoaded || !this.priceLoaded || this.isReTypeset) {
        return
      }
      // 判断下单页数，不足则弹出提示
      if (this.book.totalPage < config.minBookPagesLimitation && this.bookType === 'diarybook') {
        this.$confirm({
          title: this.$t('checkout.tipPageLimit'),
          cancelText: this.$t('button.cancel'),
          okText: this.$t('button.ok'),
          onOk: (modal) => {
            modal.close()
            this.startOrder()
          }
        })
      } else {
        this.startOrder()
      }
    },
    startOrder () {
      const order = Object.assign({}, this.order, {
        bid: this.$route.query.bid,
        tz: 8,
        couponNo: this.activeCoupon.couponNo || '',
        addressId: this.address.addressId || '',
        currency: this.currency,
        countryCode: this.$store.state.locale || 'TW',
        locale: this.$store.state.language,
        payDevice: 'pc',
        printType: this.bookType === 'diarybook' ? this.book.typeset : '',
        bookType: this.bookType
      })
      if (this.bookType !== 'diarybook') {
        delete order.binding
      }
      if (order.binding !== 'pdf') {
        if (!order.addressId) {
          this.$toast({
            message: this.$t('checkout.tipAddressFailed')
          })
          const addressEl = this.$refs.address.$el
          if (!addressEl.classList.contains('error-item')) {
            addressEl.classList.add('error-item')
          }
          return
        }
        // 地址完善提醒
        if (!this.address.phone.trim() || !this.address.country || !this.address.city || !this.address.state ||
          !this.address.line1) {
          this.$toast({
            message: this.$t('checkout.tipAddressUncompleted')
          })
          return
        }
      } else {
        if (!order.pdfEmail) {
          this.$toast({
            message: this.$t('checkout.tipAddressFailed')
          })
          const addressEl = this.$refs.pdfaddress
          if (!addressEl.classList.contains('error-item')) {
            addressEl.classList.add('error-item')
          }
          return
        }
        order.addressId = ''
        order.delivery = '0-day-delivery'
      }

      // 当前存在优惠券未使用，提示选择优惠
      const isTipped = typeof Cookie.get('check_tip') === 'undefined'
      if (isTipped && isEmpty(this.activeCoupon) && this.coupons.length) {
        Cookie.set('check_tip', true)
        this.$confirm({
          title: this.$t('checkout.tipCouponExist'),
          okText: this.$t('checkout.btnCouponCheck'),
          cancelText: this.$t('checkout.btnGoPay'),
          onOk: (modal) => {
            modal.close()
            this.showCoupon = true
          },
          onCancel: (modal) => {
            modal.close()
            this.startCheck()
          }
        })
        return
      }

      if ((this.address.countryCode === 'TW') &&
        (!this.idInfo.customsName || !this.idInfo.idNumber)) {
        this.showIdInfo = true
        return
      }

      // 当前订单免费，免邮。设置免单订单
      if (!isNaN(this.totalCost) && !Number(this.totalCost)) {
        order.free = true
        order.payMethod = 'stripe'
      }
      // Stripe Checkout: no token needed, backend returns checkout URL (same as PayPal)

      this.paidLoading = {
        text: this.$t('checkout.tipPaymentConfirming'),
        show: true
      }
      // Stripe uses new checkout API; PayPal/others use existing postOrder
      if (order.payMethod === 'stripe' && !order.free) {
        order.totalCost = this.totalCost
      }
      const apiCall = order.payMethod === 'stripe' && !order.free
        ? api.createStripeCheckout(order)
        : api.postOrder(order)
      apiCall.then(data => {
        this.paidLoading.show = false
        if (!data || data.errors) {
          const message = data.errors ? data.errors.message + '<br/>' + '错误码：' + data.errors.code : this.$t(
            'checkout.tipPaymentFailed')
          if (data.errors.code === 44513) {
            this.$confirm({
              title: '支付失敗，換張卡試試吧',
              cancel: false,
              okText: '知道了',
              onCancel: (modal) => {
                modal.close()
                this.showModal = true
              }
            })
          } else if (data.errors.code === 43012) {
            this.$toast({
              message: '書籍正在排版，請稍後刷新重試',
              position: 'bottom',
              duration: 5000
            })
          } else {
            this.$toast({
              message,
              position: 'bottom',
              duration: 5000
            })
          }
          this.order.token = ''
          return
        }

        // All payment methods redirect to external checkout URL
        if (data.approvalUrl) {
          window.location.href = data.approvalUrl
          return
        }
        // Free order or direct success — redirect to order status
        this.$toast({
          message: this.$t('checkout.tipPaymentSucceed'),
          icon: 'fa-check-circle'
        })
        // 支付成功 — purchase 事件统一在 OrderStatus.vue 触发，避免重复
        this.$router.push({
          name: 'order-status',
          query: {
            order_id: data.orderId,
            type: order.payMethod
          }
        })
      })
    },
    // stripePaid removed — using Stripe Checkout (hosted payment page)
    useCoupon (coupon) {
      if (coupon.type === 'discount' && !coupon.value && this.order.quantity > 1) {
        this.$toast({
          message: '免單優惠券僅限一本書籍',
          duration: 3500
        })
        return
      }
      if (coupon.bookType !== 'all' && this.bookType !== coupon.bookType) {
        this.$toast({
          message: '當前書籍類型暫不支持使用該優惠券'
        })
        this.showCoupon = false
        return
      }
      // 处理相片书优惠券类型
      if (coupon.bookType === 'photobook' && coupon.rules.templates && coupon.rules.templates[0] !==
        this.book.template) {
        this.$toast({
          message: '當前相片书暫不支持使用該優惠券'
        })
        this.showCoupon = false
        return
      }
      if (coupon.type === 'bulk' && this.order.quantity < 2) {
        this.$toast({
          message: this.$t('checkout.tipCouponQuantityDecline')
        })
        return
      }
      let typeset = this.book.typeset || ''
      typeset = typeset.toUpperCase()
      if (coupon.rules.typeset && (typeset.indexOf(coupon.rules.typeset) === -1)) {
        this.$toast({
          message: this.$t('checkout.tipCouponTypesetDecline')
        })
        return
      }

      this.activeCoupon = coupon
      this.showCoupon = false
      this.activeValue = {
        type: coupon.type,
        value: coupon.value
      }
    }
  }
}
</script>

<style lang="scss">
@import '../../../styles/variables';

.checkout-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 0 2rem;
}

.checkout-main {
  display: flex;
  gap: 2.5rem;
  align-items: flex-start;
}

// Left: Book info card (40% width)
.checkout-book {
  width: 40%;
  flex-shrink: 0;
  background: #fafafa;
  border-radius: 12px;
  padding: 1.5rem;
  align-self: flex-start;
  position: sticky;
  top: 80px;

  &__top {
    display: flex;
    gap: 1.25rem;
    align-items: flex-start;
  }

  &__cover {
    flex-shrink: 0;
    width: 45%;
    img {
      width: 100%;
      display: block;
      border-radius: 4px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    padding-top: 0.5rem;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #222;
    margin: 0 0 0.5rem;
    line-height: 1.4;
    word-break: break-word;
  }

  &__author {
    font-size: 13px;
    color: #888;
    margin: 0;
  }

  &__details {
    margin-top: 1.25rem;
    border-top: 1px solid #e8e8e8;
    padding-top: 1rem;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    font-size: 13px;
    color: #666;
    strong { color: #222; font-weight: 600; }
  }
}

// Skeleton loading
.checkout-book__skeleton {
  .skeleton-cover {
    width: 45%;
    padding-bottom: 60%;
    background: #e5e7eb;
    border-radius: 4px;
    margin-bottom: 1rem;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
  }

  .skeleton-info {
    margin-bottom: 1.25rem;
  }

  .skeleton-details {
    border-top: 1px solid #e8e8e8;
    padding-top: 1rem;
  }

  .skeleton-line {
    height: 14px;
    background: #e5e7eb;
    border-radius: 4px;
    margin-bottom: 10px;
    animation: skeleton-pulse 1.5s ease-in-out infinite;

    &--title {
      width: 60%;
      height: 18px;
      margin-bottom: 8px;
    }

    &--sub {
      width: 40%;
      height: 12px;
    }

    &--short {
      width: 50%;
    }
  }
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.checkout-tag {
  display: inline-block;
  padding: 2px 10px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #555;
}

// Right: Order form (60% width)
.checkout-form {
  width: 60%;
  flex-shrink: 0;
  min-width: 0;

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: #111;
    margin: 0 0 1.5rem;
  }
}

.checkout-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f5f5f5;

  > label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.75rem;
  }

  .form-control {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    padding: 10px 12px;
    font-size: 14px;
    &:focus { border-color: $el-color-primary; outline: none; box-shadow: 0 0 0 2px rgba($el-color-primary, 0.08); }
  }
}

.checkout-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  label { margin: 0; font-size: 14px; font-weight: 600; color: #333; }
}

.checkout-link {
  color: $el-color-primary;
  font-size: 13px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  &:hover { text-decoration: underline; }
}

// Binding selector
.checkout-binding {
  display: flex;
  gap: 0.75rem;
  &__item {
    padding: 10px 24px;
    border: 1.5px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { border-color: #bbb; }
    &.active {
      border-color: $el-color-primary;
      background: rgba($el-color-primary, 0.04);
      color: $el-color-primary;
      font-weight: 600;
    }
  }
}

// Coupon
.checkout-coupon-input {
  display: flex;
  gap: 8px;
  width: 60%;
  .form-control { flex: 1; }
  .btn { white-space: nowrap; border-radius: 8px; }
}

.checkout-coupon-auto {
  margin-top: 10px;
  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #666;
  }
}

.checkout-coupon-active {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 10px 14px;
  width: 60%;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;

  &__info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #15803d;
    font-size: 13px;
    font-weight: 500;
    i { font-size: 16px; }
  }

  &__remove {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    padding: 4px;
    &:hover { color: #333; }
  }
}

// Payment methods
.checkout-pay-methods {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 15px;
}

.checkout-pay-method {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #999;
  font-weight: 500;
  transition: all 0.2s;
  padding: 8px 16px;
  border: 1.5px solid transparent;
  border-radius: 8px;
  &:hover { color: #555; border-color: #ddd; }
  &.active { color: $el-color-primary; font-weight: 700; border-color: $el-color-primary; background: rgba($el-color-primary, 0.03); }
}

.checkout-pay-cards {
  display: inline-flex;
  gap: 4px;
  margin-left: 4px;
  img { height: 22px; border-radius: 3px; }
}

.checkout-pay-icon {
  height: 22px;
  border-radius: 3px;
}

.checkout-pay-sep {
  color: #ddd;
  margin: 0 8px;
}

.checkout-delivery {
  font-size: 14px;
  color: #555;
  font-weight: 600;
  margin: 0;
}

// Address: label + button inline
::v-deep .order-address.form-group {
  > label {
    display: inline;
    margin-right: 10px;
  }
  > label + div {
    display: inline;
  }
}

// Note textarea
.checkout-note {
  resize: vertical;
  min-height: 36px;
  max-height: 160px;
}

// ID info
.checkout-idinfo {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  label { margin: 0; font-size: 13px; color: #666; }
  &__name { font-size: 13px; color: #333; }
}

// Tips
.checkout-tips {
  border-bottom: none;
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    label { cursor: pointer; margin: 0; }
    i { color: #999; font-size: 16px; }
  }
  &__body {
    margin-top: 0.75rem;
    p { font-size: 13px; color: #888; margin-bottom: 4px; }
  }
}

// Fixed bottom bar
.checkout-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #eee;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
  z-index: 50;
  padding: 12px 0;

  &__inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-shrink: 0;
  }

  &__total {
    text-align: right;
    white-space: nowrap;
    span { font-size: 13px; color: #666; }
    strong { font-size: 22px; color: $el-color-primary; margin-left: 4px; }
  }

  &__meta {
    font-size: 12px;
    color: #999;
    white-space: nowrap;
  }
}

.dialog-close-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 14px;

  &:hover { background: #e5e7eb; color: #374151; }
}

.checkout-price__items {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

// Mobile responsive
@media (max-width: 767px) {
  .checkout-page {
    padding: 1rem 16px 100px;
  }

  .checkout-main {
    flex-direction: column;
    gap: 1.5rem;
  }

  .checkout-book {
    width: 100%;
    position: static;
    padding: 1rem;

    &__cover {
      width: 35%;
    }

    &__title {
      font-size: 14px;
    }
  }

  .checkout-form {
    width: 100%;

    &__title {
      font-size: 18px;
      margin-bottom: 1rem;
    }
  }

  .checkout-coupon-input,
  .checkout-coupon-active {
    width: 100%;
  }

  .checkout-binding {
    flex-wrap: wrap;

    &__item {
      padding: 8px 16px;
      font-size: 13px;
    }
  }

  .checkout-footer {
    &__inner {
      padding: 0 16px;
      gap: 0;
      flex-wrap: nowrap;
      align-items: flex-start;
    }

    &__m-left {
      flex: 1;
      min-width: 0;
    }

    &__right {
      align-self: flex-start;
      padding-top: 2px;
    }

    &__m-total {
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;

      span { font-size: 13px; color: #666; }
      strong { font-size: 18px; color: $el-color-primary; }
      i { font-size: 12px; color: #999; margin-left: 2px; }
    }

    &__m-delivery {
      font-size: 11px;
      color: #999;
      margin-top: 2px;
    }

    &__m-details {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      margin-top: 4px;
      padding-top: 4px;
      border-top: 1px solid #f0f0f0;

      span {
        font-size: 11px;
        color: #888;
        white-space: nowrap;
      }
    }

    &__right {
      flex-shrink: 0;
    }

    .checkout-submit__btn {
      padding: 8px 20px;
      font-size: 14px;
    }
  }
}

.checkout-price__item {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  &.text-green { color: #16a34a; }
}

.checkout-submit__btn {
  min-width: 140px;
  padding: 10px 28px;
  font-size: 15px;
  border-radius: 10px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

// Add bottom padding to page so content isn't hidden behind fixed footer
.checkout-page {
  padding-bottom: 80px !important;
}

// PDF email
.pdf-email {
  &.error-item {
    position: relative;
    animation-name: pulse;
    animation-duration: .5s;
    animation-fill-mode: both;
    &:after {
      content: '';
      position: absolute;
      top: -10px; bottom: -10px; left: -10px; right: -10px;
      border: 1px solid $el-color-primary;
      pointer-events: none;
      border-radius: 8px;
    }
  }
}

// Responsive
@media (max-width: 767px) {
  .checkout-main {
    flex-direction: column;
    gap: 1.5rem;
  }
  .checkout-book {
    width: 100% !important;
  }
  .checkout-form {
    width: 100% !important;
  }
  .checkout-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  .checkout-submit {
    text-align: center;
  }
}

// Coupon popup: center and theme color (global scope since popup is fixed/portal)
.popup-wrapper .popup-content {
  bottom: auto !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}

.coupon-item .coupon-item__discount {
  background: $gradient-purple-blue !important;
}

// Coupon 弹窗 body 内边距：上 100px，其余 50px
.coupon-popup-body {
  padding: 100px 50px 50px !important;

  @media (max-width: 600px) {
    // 手机屏幕上压缩一些
    padding: 40px 16px 16px !important;
  }
}

// Keep Trade styles for modals
@import '../../../styles/layout/Trade';
</style>
